import React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Box, Stack, type SxProps } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import AppLogo from '@components/AppLogo';
import DashboardBarButton from './DashboardBarButton';
import ParticipantsButton from './ParticipantsButton';
import FullScreenButton from './FullScreenButton';
import UserAvatarButton from '@components/UserAvatarButton';
import AdminTools from './AdminTools';
import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';
import { IDashboardTopBarContentView } from './interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantScopes from '@constants/scopes';
import { getClientSpecificDashboardTopBarRight } from '@helpers/clientSpecificUtils';
import { isSinglePlayerEvent } from '@helpers/events';

import { env } from 'env';

import { useStyles } from './styles';
import { useParticipantList } from '@core/dashboard/components/HomeBottomBar/Participants';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { useMytaverseEvent } from '@core/dashboard/providers';
import {
  useClientSpecificOnlineUsersVisibility,
  useClientSpecificTeleportVisibility,
} from '@hooks/clientSpecificUtils';

const {
  REACT_APP_SHOW_LEFT_BAR,
  REACT_APP_SHOW_PROFILE,
  REACT_APP_SHOW_ADMIN_TOOLS,
  REACT_APP_EXTERNAL_EVENT_TAG,
} = env;

const DashboardTopBarContentView = ({
  currentRoom,
  isOpenChat,
  hasMediaStream,
  participantTotal,
  currentEvent,
}: IDashboardTopBarContentView) => {
  const classes = useStyles({
    isOpenChat,
    hasMediaStream,
  });
  const { userPermissions, sendMessageToUnreal } = useMytaverse();
  const { isTeleportingToRoomByUnreal, teleportEnabled } = useMytaverseEvent();
  const { isTeleportFeatureEnabled } = useClientSpecificTeleportVisibility();
  const { toggleOpen: toggleParticipants } = useParticipantList();
  const isTeleportEnabled = teleportEnabled && isTeleportFeatureEnabled;
  const showLeftMenu = isSinglePlayerEvent(currentEvent) || currentRoom;
  const showAdminTools = REACT_APP_SHOW_ADMIN_TOOLS === 'true';
  const showLeftBar = REACT_APP_SHOW_LEFT_BAR === 'true';
  const { isOnlineUsersEnabled } = useClientSpecificOnlineUsersVisibility();
  const showProfile =
    REACT_APP_SHOW_PROFILE === 'true' ||
    (REACT_APP_EXTERNAL_EVENT_TAG
      ? currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG)
      : false);

  //TODO: when it's active, it should be added to mobile as well
  const isAdminToolEnabled =
    showAdminTools &&
    userPermissions?.includes(ParticipantScopes.HubAdminTools);
  const allowOnMobileForExternalEvent =
    !!REACT_APP_EXTERNAL_EVENT_TAG &&
    currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG);

  if (isMobileOnly && !allowOnMobileForExternalEvent) {
    return null;
  }

  const renderTopRightActions = (customSx?: SxProps) => (
    <MytaverseAppBar
      position={AppBarPositionEnum.TopRight}
      isCollapsible={false}
      sx={customSx}
    >
      <FullScreenButton />
      {isAdminToolEnabled || true ? <AdminTools /> : null}
      {showProfile ? <UserAvatarButton /> : null}
    </MytaverseAppBar>
  );

  const clientSpecificDashboardTopBar = getClientSpecificDashboardTopBarRight(
    renderTopRightActions,
  );

  const handleTeleport = () => {
    sendMessageToUnreal({
      action: WebsocketAction.BeamMeUp,
      state: isTeleportingToRoomByUnreal,
    });
  };

  return (
    <>
      {showLeftBar ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.TopLeft}
          sx={classes.leftBar}
          isCollapsible={false}
          isGroup={false}
        >
          {showLeftMenu ? (
            <>
              <Box sx={classes.root} component="div">
                <DashboardBarButton
                  buttonIcon={<LocationOnIcon />}
                  onClick={isTeleportEnabled ? handleTeleport : undefined}
                >
                  {currentRoom?.name || 'Lobby'}
                </DashboardBarButton>
                {isOnlineUsersEnabled ? (
                  <ParticipantsButton
                    onClick={toggleParticipants}
                    total={participantTotal}
                  />
                ) : null}
              </Box>
            </>
          ) : (
            <Box sx={classes.rootLoading}>
              <Stack flexDirection="row" alignItems="center">
                <AppLogo />
              </Stack>
            </Box>
          )}
        </MytaverseAppBar>
      ) : null}
      {clientSpecificDashboardTopBar
        ? clientSpecificDashboardTopBar
        : renderTopRightActions()}
    </>
  );
};

export default DashboardTopBarContentView;
